import { useSnackbar } from 'notistack'
import { SubmitHandler, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Expertise } from '@/core/models/expertise.model'
import { useDiagnoseExpertiseMutation } from '@/core/services/expertise'

const schema = z.object({
  conclusion: z.string().min(1),
  diagnostic: z.string().min(1),
})

export type ExpertiseFormData = z.infer<typeof schema>

export function useExpertiseForm(expertise: Expertise) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [diagnoseExpertise, { isLoading: isDiagnosingExpertise }] = useDiagnoseExpertiseMutation()

  const rhForm = useForm<ExpertiseFormData>({
    defaultValues: {
      conclusion: expertise.conclusion ?? '',
      diagnostic: expertise.diagnostic ?? '',
    },
    resolver: zodResolver(schema),
  })

  const onSubmit: SubmitHandler<ExpertiseFormData> = async (data) => {
    try {
      await diagnoseExpertise({ id: expertise._id, conclusion: data.conclusion, diagnostic: data.diagnostic }).unwrap()
      enqueueSnackbar(t('notification.expertise.update.success'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(t('notification.expertise.update.error'), { variant: 'error' })
    }
  }

  return { isSubmitting: isDiagnosingExpertise, rhForm, onSubmit }
}

export function useExpertiseFormContext() {
  return useFormContext<ExpertiseFormData>()
}
